import * as Yup from 'yup';
import { Field, FieldProps } from 'formik';
import type { FormikProps } from 'formik';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@round/ui-kit';
import { wave, waveQueries } from '@round/api';
import { usePublicContentTags } from './usePublicContentTags';
import { localizeLanguageOptions } from './languageOptions';
import { showNotification } from '@/utility/showNotification';
import { formatHandle } from '@/utility/formatHandle';
import { waveSelectStyles } from './waveSelectStyles';
import { ChipMultiSelect, ChipSingleSelect, ChipOption } from './ChipSelect';
import { bundleOfferSummary, bundleOffersBody, bundleOffersValidation } from './bundleOffers';
import SignupWizard, {
    SignupFormValues,
    TagOption,
    WizardStep,
    baseAccountShape,
    baseValidationShape,
    isBundleOfferEmpty,
} from './SignupWizard';
import {
    AccountLanguagesStep,
    AccountsStep,
    BasicsStep,
    BundleOffersStep,
    FieldLabel,
    NOTIFICATION_CHANNEL_OPTIONS,
    RequiredLabel,
    StayingInTouchStep,
    SummaryRow,
    TermsSection,
    WhereBasedStep,
    accountLabel,
    labelsOf,
    platformLabel,
} from './steps';

const MIN_TAGS = 2;
const MAX_TAGS = 5;

// Choice vocabularies — mirror app/wave/constants.py (ROU-873).
const EXPERIENCE_OPTIONS: ChipOption<wave.CreatorExperienceLevel>[] = [
    { value: 'just_starting', label: 'Just starting out' },
    { value: 'occasional_no_campaign', label: 'Post occasionally, no paid campaigns' },
    { value: 'consistent_no_paid', label: 'Consistent, no paid campaigns' },
    { value: 'gifted_done', label: 'Done gifted collaborations' },
    { value: 'few_paid', label: 'A few paid campaigns' },
    { value: 'regular_paid', label: 'Regular paid campaigns' },
    { value: 'professional', label: 'A significant part of my work' },
];

const CONTENT_FORMAT_OPTIONS: ChipOption<wave.CreatorContentFormat>[] = [
    { value: 'short_form_video', label: 'Short-form video' },
    { value: 'reels', label: 'Reels' },
    { value: 'stories', label: 'Stories' },
    { value: 'photo_post', label: 'Photo posts' },
    { value: 'carousel', label: 'Carousels' },
    { value: 'long_form_video', label: 'Long-form video' },
    { value: 'event_recap', label: 'Event recaps' },
];

const CAMPAIGN_TYPE_OPTIONS: ChipOption<wave.CreatorCampaignType>[] = [
    { value: 'fixed_fee', label: 'Fixed-fee paid content' },
    { value: 'cpm', label: 'CPM / view-based' },
    { value: 'experience', label: 'Experience-based' },
    { value: 'gifted', label: 'Gifted product' },
    { value: 'event_coverage', label: 'Event coverage' },
    { value: 'ugc', label: 'User-generated content' },
    { value: 'artist_release', label: 'Artist / music-release' },
];

export const buildValidationSchema = () =>
    Yup.object().shape({
        ...baseValidationShape,
        accounts: Yup.array()
            .of(
                Yup.object().shape({
                    ...baseAccountShape,
                    contentLanguages: Yup.array().min(1, 'Pick at least one language'),
                })
            )
            .min(1, 'Add at least one social account'),
        contentTags: Yup.array()
            .min(MIN_TAGS, `Select at least ${MIN_TAGS} tags`)
            .max(MAX_TAGS, `Select up to ${MAX_TAGS} tags`),
        ...bundleOffersValidation(),
    });

// This page has no section config, so the vocabulary comes from the public languages list.
const useLanguageOptions = () => {
    const { data, isLoading, isError } = useQuery(waveQueries.language.options.list());
    useEffect(() => {
        if (isError) {
            showNotification('Could not load languages', 'error');
        }
    }, [isError]);
    return { options: localizeLanguageOptions(data ?? []), isLoading };
};

const LanguagesStep = () => {
    const { options, isLoading } = useLanguageOptions();
    return <AccountLanguagesStep options={options} isLoading={isLoading} />;
};

const LanguageSummaryRows = ({ formik, onEdit }: { formik: FormikProps<SignupFormValues>; onEdit: () => void }) => {
    const { options } = useLanguageOptions();
    return (
        <>
            {formik.values.accounts.map((account, index) => (
                <SummaryRow
                    key={index}
                    label={
                        formik.values.accounts.length > 1 ? `Languages · ${accountLabel(account, index)}` : 'Languages'
                    }
                    value={labelsOf(options, account.contentLanguages)}
                    onEdit={onEdit}
                />
            ))}
        </>
    );
};

const ContentStep = ({ formik }: { formik: FormikProps<SignupFormValues> }) => {
    const { tags, isLoading: areTagsLoading } = usePublicContentTags();
    const tagOptions: TagOption[] = tags.map((tag) => ({ value: tag.id, label: tag.name }));
    return (
        <div className="flex flex-col gap-6">
            <label className="wm-field">
                <RequiredLabel>
                    Pick {MIN_TAGS}&ndash;{MAX_TAGS} tags that describe your content
                </RequiredLabel>
                <Select
                    styles={waveSelectStyles}
                    inputId="contentTags"
                    isMulti
                    menuPosition="fixed"
                    captureMenuScroll={false}
                    isDisabled={formik.isSubmitting}
                    isLoading={areTagsLoading}
                    options={tagOptions}
                    value={formik.values.contentTags}
                    onChange={(options) => formik.setFieldValue('contentTags', (options as TagOption[]) ?? [])}
                />
                <span className="wm-field__hint">
                    Tag yourself accurately. Inaccurate tags may get your application rejected.
                </span>
                {formik.touched.contentTags && typeof formik.errors.contentTags === 'string' && (
                    <span className="text-xs text-wm-danger">{formik.errors.contentTags}</span>
                )}
            </label>
            <div>
                <FieldLabel>Formats you&apos;re comfortable with</FieldLabel>
                <ChipMultiSelect
                    options={CONTENT_FORMAT_OPTIONS}
                    values={formik.values.contentFormats}
                    onChange={(v) => formik.setFieldValue('contentFormats', v)}
                />
            </div>
        </div>
    );
};

const stepIndex = (steps: WizardStep[], id: string) => steps.findIndex((step) => step.id === id);

// Follows the round-campaign-manager onboarding flow, mapped to the wave
// application API fields (ROU-873). Optional steps have no gating fields.
// Steps are addressed by id, not position, so reordering them can't silently
// re-point the summary's Edit buttons.
export const buildSteps = (): WizardStep[] => {
    const steps: WizardStep[] = [
        {
            id: 'basics',
            title: 'The basics',
            blurb: 'Who you are and where to reach you.',
            fields: ['firstName', 'lastName', 'email', 'confirmEmail', 'isOver18'],
            render: () => <BasicsStep />,
        },
        {
            id: 'accounts',
            title: 'Where can we find you?',
            blurb: 'The social accounts you post on.',
            fields: [],
            accountFields: ['platform', 'handle'],
            render: () => <AccountsStep />,
        },
        {
            id: 'languages',
            title: 'Languages you post in',
            blurb: 'So we match you with campaigns your audience understands.',
            fields: [],
            accountFields: ['contentLanguages'],
            render: () => <LanguagesStep />,
        },
        {
            id: 'location',
            title: "Where you're based",
            blurb: 'So we send you the right opportunities.',
            fields: ['locationId'],
            render: ({ formik, location, setLocation }) => (
                <WhereBasedStep
                    location={location}
                    onLocationChange={(next, locationId) => {
                        setLocation(next);
                        formik.setFieldValue('locationId', locationId);
                    }}
                />
            ),
        },
        {
            id: 'content',
            title: 'Your content',
            blurb: 'What you make and the formats you post.',
            fields: ['contentTags'],
            render: ({ formik }) => <ContentStep formik={formik} />,
        },
        {
            id: 'journey',
            title: 'Your journey',
            blurb: 'Where you are as a creator.',
            fields: [],
            render: ({ formik }) => (
                <div>
                    <FieldLabel>Where are you in your creator journey?</FieldLabel>
                    <ChipSingleSelect
                        options={EXPERIENCE_OPTIONS}
                        value={formik.values.experienceLevel}
                        onChange={(v) => formik.setFieldValue('experienceLevel', v)}
                        layout="stack"
                    />
                </div>
            ),
        },
        {
            id: 'campaigns',
            title: "Campaigns you're up for",
            blurb: 'Types, plus anything brands should know.',
            fields: [],
            render: ({ formik }) => (
                <div className="flex flex-col gap-6">
                    <div>
                        <FieldLabel>Campaign types you&apos;re up for</FieldLabel>
                        <ChipMultiSelect
                            options={CAMPAIGN_TYPE_OPTIONS}
                            values={formik.values.campaignTypes}
                            onChange={(v) => formik.setFieldValue('campaignTypes', v)}
                        />
                    </div>
                    <label className="wm-field">
                        <span className="wm-field__label">Anything brands should know about usage or rates?</span>
                        <Field name="commercialNotes">
                            {({ field }: FieldProps) => <textarea className="wm-input wm-textarea" {...field} />}
                        </Field>
                    </label>
                    <label className="wm-field">
                        <span className="wm-field__label">Anything you won&apos;t promote or do?</span>
                        <Field name="boundaries">
                            {({ field }: FieldProps) => <textarea className="wm-input wm-textarea" {...field} />}
                        </Field>
                    </label>
                </div>
            ),
        },
        // Sits after the accounts step because every bundle binds to the accounts entered there.
        {
            id: 'bundleOffers',
            title: 'Bundle deals',
            blurb: 'Optional. One price for a set of posts, if you offer that.',
            fields: ['bundleOffers'],
            render: () => <BundleOffersStep />,
        },
        {
            id: 'stayingInTouch',
            title: 'Staying in touch',
            blurb: 'How and when we reach you.',
            fields: [],
            render: () => <StayingInTouchStep />,
        },
        {
            id: 'review',
            title: 'Review & submit',
            blurb: 'Check everything, then send it to the Wave team.',
            fields: ['termsAccepted'],
            render: ({ formik, location, setStep }) => {
                const goTo = (id: string) => {
                    const index = stepIndex(steps, id);
                    if (index !== -1) {
                        setStep(index);
                    }
                };
                const bundleOffers = formik.values.bundleOffers.filter((offer) => !isBundleOfferEmpty(offer));
                return (
                    <>
                        <div className="rounded-wm-lg border border-wm-line bg-wm-surface px-4 py-1">
                            <SummaryRow
                                label="Name"
                                value={`${formik.values.firstName} ${formik.values.lastName}`.trim()}
                                onEdit={() => goTo('basics')}
                            />
                            <SummaryRow label="Email" value={formik.values.email} onEdit={() => goTo('basics')} />
                            <SummaryRow label="Phone" value={formik.values.phoneNumber} onEdit={() => goTo('basics')} />
                            <SummaryRow
                                label="Socials"
                                value={formik.values.accounts
                                    .filter((a) => a.handle.trim())
                                    .map(
                                        (a) =>
                                            `${platformLabel(a.platform)} · ${formatHandle(
                                                a.platform,
                                                a.handle.trim()
                                            )}`
                                    )
                                    .join(', ')}
                                onEdit={() => goTo('accounts')}
                            />
                            <LanguageSummaryRows formik={formik} onEdit={() => goTo('languages')} />
                            <SummaryRow
                                label="Location"
                                value={location.city?.label ?? location.state?.label ?? location.country?.label ?? ''}
                                onEdit={() => goTo('location')}
                            />
                            <SummaryRow
                                label="Content tags"
                                value={formik.values.contentTags.map((t) => t.label).join(', ')}
                                onEdit={() => goTo('content')}
                            />
                            <SummaryRow
                                label="Formats"
                                value={labelsOf(CONTENT_FORMAT_OPTIONS, formik.values.contentFormats)}
                                onEdit={() => goTo('content')}
                            />
                            <SummaryRow
                                label="Experience"
                                value={
                                    EXPERIENCE_OPTIONS.find((o) => o.value === formik.values.experienceLevel)?.label ??
                                    ''
                                }
                                onEdit={() => goTo('journey')}
                            />
                            <SummaryRow
                                label="Campaign types"
                                value={labelsOf(CAMPAIGN_TYPE_OPTIONS, formik.values.campaignTypes)}
                                onEdit={() => goTo('campaigns')}
                            />
                            {bundleOffers.map((offer, index) => (
                                <SummaryRow
                                    key={index}
                                    label={`Bundle ${index + 1}`}
                                    value={bundleOfferSummary(offer, formik.values.accounts)}
                                    onEdit={() => goTo('bundleOffers')}
                                />
                            ))}
                            <SummaryRow
                                label="Staying in touch"
                                value={labelsOf(NOTIFICATION_CHANNEL_OPTIONS, formik.values.notificationChannels)}
                                onEdit={() => goTo('stayingInTouch')}
                            />
                        </div>
                        <TermsSection />
                    </>
                );
            },
        },
    ];
    return steps;
};

export const buildBody = (values: SignupFormValues, referralCode?: string): wave.PostCreatorApplicationBody => ({
    first_name: values.firstName.trim(),
    last_name: values.lastName.trim(),
    email: values.email.trim(),
    phone_number: values.phoneNumber.trim(),
    location_id: values.locationId as number,
    other_socials_notes: values.otherSocials.trim(),
    content_tags: values.contentTags.map((tag) => tag.value),
    accounts: values.accounts.map((account) => ({
        platform: account.platform,
        handle: account.handle.trim(),
        content_languages: account.contentLanguages,
    })),
    referral_code: referralCode,
    is_over_18: values.isOver18,
    happy_to_travel: values.happyToTravel,
    commercial_notes: values.commercialNotes.trim(),
    boundaries: values.boundaries.trim(),
    experience_level: values.experienceLevel,
    content_formats: values.contentFormats,
    campaign_types: values.campaignTypes,
    notification_channels: values.notificationChannels,
    availability: values.availability,
    bundle_offers: bundleOffersBody(values),
    terms_accepted: values.termsAccepted,
});

/** Generic (pool-agnostic) signup with the full profile wizard. */
const SignupPage = () => {
    const steps = useMemo(() => buildSteps(), []);
    const validationSchema = useMemo(() => buildValidationSchema(), []);

    return <SignupWizard steps={steps} validationSchema={validationSchema} buildBody={buildBody} />;
};

export default SignupPage;
